import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { cn, Badge } from '@bliks-io/ui';
import { useProfile } from '@/api/queries/useProfile';
import { useAuthStore } from '@/stores/auth-store';

import { HeaderBreadcrumb } from './HeaderBreadcrumb';
// import { NotificationMenu } from './NotificationMenu'; // Hidden until functionality is implemented
import { UserMenu } from './UserMenu';

const MS_PER_DAY = 86_400_000;

function useTrialDaysLeft(): number | null {
  const tenant = useAuthStore((s) => s.tenant);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  if (isSuperAdmin() || !tenant?.expiresAt) return null;

  return Math.ceil((new Date(tenant.expiresAt).getTime() - Date.now()) / MS_PER_DAY);
}

function getTrialLabelKey(daysLeft: number): [string, Record<string, unknown>?] {
  if (daysLeft > 1) return ['common.trialDaysLeft', { days: daysLeft }];
  if (daysLeft === 1) return ['common.trialLastDay'];
  return ['common.trialExpired'];
}

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const { t } = useTranslation();
  const { data: user } = useProfile();
  const daysLeft = useTrialDaysLeft();

  return (
    <header className={cn('flex h-14 shrink-0 items-center bg-sidebar px-4 w-full', className)}>
      <div className="flex items-center flex-1 gap-2">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img src="/assets/images/logo.svg" alt="BLIKS IO Logo" className="w-auto h-6" />
        </div>

        {/* Breadcrumb */}
        <HeaderBreadcrumb />
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        {daysLeft !== null && (
          <Badge colorScheme="error" shape="pill" className="gap-1.5">
            <Clock className="h-3 w-3" />
            {t(...getTrialLabelKey(daysLeft))}
          </Badge>
        )}
        {/* <NotificationMenu /> */}
        {user && <UserMenu />}
      </div>
    </header>
  );
}
